<template>
    <div class="container mx-auto text-center max-w-lg lg:max-w-sm md:mt-5 lg:mt-0">
        <div class="md:border md:border-black bg-white py-5">
            <h2 class="pb-3" v-text="$t('PAYMENT_SUMMARY_FINAL_CHECKS')"></h2>

            <!-- Coupon Info -->
            <div class="PaymentSummary" v-if="specialDatesCouponApplied">
                <p class="PaymentSummary__text"> {{ $t('SPECIAL_DATES_GIFT_COUPON', { currency, amount: couponDiscountValue(specialDatesCoupon) })}}</p>
            </div>
            <div else>
                <div class="PaymentSummary" v-if="multiCoupon && isSubscription && validDiscount && !isPopMulti">
                    <p class="PaymentSummary__text"> {{ multiGiftingText}}</p>
                </div>
                <div class="PaymentSummary" v-if="singleCoupon && !isSubscription && validSingleCoupon && !isPopOneOff">
                    <p class="PaymentSummary__text"> {{ singleGiftingText}}</p>
                </div>
            </div>

            <!-- Send summary -->
            <div class="pb-3">
                {{ $t('PAYMENT_SUMMARY_SENDING') }}
                <div class="font-light" v-text="summaryString"></div>
            </div>

            <!-- Address -->
            <div class="pb-3">
                To:
                <div class="font-light">
                    <div v-text="fullName"></div>
                    <div v-text="address.address1"></div>
                    <div v-if="address.address2 && address.address2.length" v-text="address.address2"></div>
                    <div v-text="address.postcode"></div>
                </div>
            </div>

            <!-- First Delivery -->
            <div class="pb-3">
                <div v-if="isSubscription" v-text="$t('PAYMENT_SUMMARY_STARTING_ON')"></div>
                <div v-else v-text="$t('PAYMENT_SUMMARY_DELIVERY_ON')"></div>
                <div class="font-light" v-html="formattedDeliveryDate"></div>
            </div>

            <!-- Final Delivery -->
            <div v-if="isSubscription" class="pb-3">
                {{ $t('PAYMENT_SUMMARY_FINAL_DELIVERY') }}
                <div class="font-light" v-html="lastDeliveryDate"></div>
            </div>

            <!-- Vase -->
            <div v-if="isVaseIncluded && !isPopMulti" class="pb-3">
                {{ $t('PAYMENT_SUMMARY_ALONG_WITH') }}
                <div class="font-light" v-if="isVaseFree">
                    {{ $t('PAYMENT_SUMMARY_A_FREE_VASE') }}
                </div>
                <div class="font-light" v-else>
                    {{ $t('PAYMENT_SUMMARY_A_FAB_FREDDIES_VASE') }}
                </div>
            </div>

            <!-- Gift card -->
            <div class="pb-3">
                {{ $t('PAYMENT_SUMMARY_PERSONAL_NOTE') }}
                <div class="font-light" v-text="messageTo"></div>
                <div class="font-light" v-text="messageBody"></div>
                <div class="font-light" v-text="messageFrom"></div>
            </div>

            <!-- Notification number -->
            <div class="pb-3">
                <div v-text="notifierTitleString"></div>
                <div class="font-light" v-text="notificationMobileNumber"></div>
            </div>

            <!-- Summary -->
            <div class="pb-3">
                <div v-if="isSubscription">
                    {{ totalDeliveries }} {{ $t('PAYMENT_SUMMARY_BOXES') }}
                </div>
                <div v-else v-text="$t('PAYMENT_SUMMARY_ONE_OFF',{ boxName: getBoxName })"></div>
                <div class="font-light">{{currency}}{{ totalDeliveriesPrice }}</div>
            </div>

            <div v-if="isVaseIncluded && !isPopMulti" class="pb-3">
                {{ $t('PAYMENT_SUMMARY_FAB_VASE') }}
                <div class="font-light" v-if="isVaseFree" v-text="$t('PAYMENT_SUMMARY_FREE')"/>
                <div class="font-light" v-else>{{currency}}{{ vasePrice }}</div>
            </div>

            <div v-for="(item, key) in additionalProducts" :key="key" class="pb-3">
                {{item.quantity}} x {{item.title}}
                <div class="font-light">{{currency}}{{item.price}}</div>
            </div>

            <div>
                {{ $t('PAYMENT_SUMMARY_TOTAL_PRICE') }}
                <div class="font-light">{{currency}}{{ totalPrice }}</div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { formatDate } from '@/helpers/DateHelpers';
import * as Dates from '@/constants/Dates';
import { PRODUCT_TYPE_MULTI,
    PRODUCT_FAMILY_BUMPER,
    PRODUCT_TYPE_BUMPER_MULTI,
    PRODUCT_FAMILY_STANDARD,
    PRODUCT_TYPE_SINGLE,
    PRODUCT_TYPE_BUMPER_SINGLE,
    PRODUCT_TYPE_POP_SINGLE, PRODUCT_TYPE_POP_MULTI, PRODUCT_FAMILY_LITE } from '@/constants/Gifting';

export default {
    name: 'PaymentSummary',
    data() {
        return {
            specialDatesCouponApplied: false,
        };
    },
    methods: {
        couponDiscountValue(coupon) {
            if (coupon?.discountType === 'percentage') {
                return this.totalDeliveriesPrice * (coupon?.discount / 100);
            } else if (coupon?.discountType === 'amount') {
                return coupon?.discount * 0.01;
            }
            return 0;
        },
        calculateDiscountAmount() {
            let currentCoupon;
            if (this.specialDatesEnabled && this.specialDatesCoupon && this.isPopCoupon) {
                currentCoupon = this.specialDatesCoupon;
                this.specialDatesCouponApplied = true;
            } else {
                currentCoupon = this.isSubscription ? this.multiCoupon : this.singleCoupon;
            }
            if (this.specialDatesEnabled && this.specialDatesCoupon && this.couponDiscountValue(currentCoupon) <= this.couponDiscountValue(this.specialDatesCoupon)) {
                currentCoupon = this.specialDatesCoupon;
                this.specialDatesCouponApplied = true;
            } else {
                if ((this.selectedBoxName === PRODUCT_TYPE_BUMPER_MULTI || this.selectedBoxName === PRODUCT_TYPE_BUMPER_SINGLE) &&
                    !currentCoupon?.product_families.includes(PRODUCT_FAMILY_BUMPER)) {
                    return 0;
                }
                if ((this.selectedBoxName === PRODUCT_TYPE_MULTI || this.selectedBoxName === PRODUCT_TYPE_SINGLE) &&
                    !currentCoupon?.product_families.includes(PRODUCT_FAMILY_STANDARD)) {
                    if (currentCoupon?.product_families.length) return 0;
                }
                if ((this.selectedBoxName === PRODUCT_TYPE_POP_MULTI || this.selectedBoxName === PRODUCT_TYPE_POP_SINGLE) &&
                    !currentCoupon?.product_families.includes(PRODUCT_FAMILY_LITE)) {
                    return 0;
                }
            }

            const discountType = currentCoupon?.discountType;
            const discountValue = currentCoupon?.discount;

            if (discountType === 'percentage') {
                const saving = this.totalDeliveriesPrice * (discountValue / 100);
                return saving.toFixed(2);
            } else if (discountType === 'amount') {
                const saving = discountValue * 0.01;
                return saving.toFixed(2);
            }
            return 0;
        },
        calculateTotalPrice() {
            const validSingleGiftDiscount = this.singleCoupon;
            const validMultiGiftDiscount = this.multiCoupon && this.validDiscount;
            const discountCriteria = this.isSubscription ? validMultiGiftDiscount : validSingleGiftDiscount;
            const totalPrice = discountCriteria || this.specialDatesCoupon ? this.discountedPrice : Number(this.totalDeliveriesPrice);

            if ((this.isVaseIncluded && this.paidVaseSelected && !this.isFreeVase) || (this.isFreeVase && !this.isSubscription && this.isAllowedBoxType && this.paidVaseSelected)) {
                return totalPrice + Number(this.vasePrice);
            }
            return (this.additionalProductsCost + totalPrice).toFixed(2);
        },
        couponDiscount(coupon) {
            if (coupon.discountType === 'percentage') {
                return `${coupon.discount}%`;
            } else if (coupon.discountType === 'amount') {
                const saving = coupon.discount * 0.01;
                return `${this.currency}${saving}`;
            }
        },
    },
    mounted() {
        this.$store.dispatch('gifting/fetchPrices');
        this.$store.dispatch('gifting/fetchGiftingCoupons');
    },
    computed: {
        ...mapGetters({
            address: 'address/getSelectedGiftingAddress',
            additionalProducts: 'gifting/getAdditionalProducts',
            additionalProductsCost: 'gifting/getAdditionalProductsTotalCost',
            currency: 'localisation/getCurrency',
            deliveryDate: 'gifting/getDeliveryDateSelected',
            frequency: 'gifting/getFrequencyForSubscription',
            isSubscription: 'gifting/isSubscription',
            lastDeliveryDate: 'gifting/getSubscriptionPredictedLastDeliveryDate',
            messageBody: 'gifting/getMessageBody',
            messageFrom: 'gifting/getMessageFrom',
            messageTo: 'gifting/getMessageTo',
            multiCoupon: 'gifting/getMultiGiftCoupon',
            notificationMobileNumber: 'gifting/getNotifierNumber',
            notifier: 'gifting/getNotifier',
            numberOfMonths: 'gifting/getNumberOfMonthsForSubscription',
            paidVaseSelected: 'gifting/isPaidVaseAttached',
            singleCoupon: 'gifting/getSingleGiftCoupon',
            specialDatesCoupon: 'gifting/getSpecialDatesGiftCoupon',
            totalDeliveriesPrice: 'gifting/getTotalPriceForDeliveries',
            totalDeliveries: 'gifting/getNumberOfDeliveriesForSubscription',
            vasePrice: 'gifting/getSelectedVasePrice',
            isVaseEnabled: 'account/getGiftingVaseEnabled',
            selectedBoxName: 'gifting/getSelectedBoxName',
            specialDatesEnabled: 'account/getSpecialDatesEnabled',
            isFreeVase: 'account/isFreeVase',
            products: 'gifting/getGiftProducts',
            isPopMultiEnabled: 'account/getPopMultiGift',
        }),
        fullName() {
            return `${this.address.first_name} ${this.address.last_name}`;
        },
        deliveriesPrice: function() {
            return Number(this.totalDeliveriesPrice).toFixed(2);
        },
        formattedDeliveryDate() {
            return formatDate(this.deliveryDate, Dates.DAY_DATE_MONTH);
        },
        isVaseIncluded() {
            if (this.selectedBoxName === PRODUCT_TYPE_POP_SINGLE && this.isFreeVase) return false;
            return (this.isVaseEnabled && (this.isSubscription || this.paidVaseSelected)) || this.isFreeVase;
        },
        isVaseFree() {
            return (this.isSubscription) || (this.isFreeVase && !this.isAllowedBoxType && !this.isSubscription);
        },
        isAllowedBoxType() {
            return ![PRODUCT_TYPE_SINGLE, PRODUCT_TYPE_BUMPER_SINGLE].includes(this.selectedBoxName);
        },
        notifierTitleString() {
            return this.notifier.toLowerCase() === this.$t('PAYMENT_SUMMARY_YOU')
                ? this.$t('PAYMENT_SUMMARY_COURIER')
                : this.$t('PAYMENT_SUMMARY_COURIER_WILL', { 'recipientFirstName': this.$t(`${this.recipientFirstName}`) });
        },
        summaryString() {
            if (!this.isSubscription) {
                return this.$t('PAYMENT_SUMMARY_ONE_OFF_BOX', { boxType: this.getBoxName });
            }

            const monthString = this.numberOfMonths === 1 ? this.$t('PAYMENT_SUMMARY_MONTH') : this.$t('PAYMENT_SUMMARY_MONTHS');
            const frequencyString = this.frequency.toLowerCase();

            return this.$t('PAYMENT_SUMMARY_OF_DELIVERIES', {
                'numberOfMonths': this.$t(`${this.numberOfMonths}`),
                'monthString': this.$t(`${monthString}`),
                'frequencyString': this.$t(`${frequencyString}`),
            });
        },
        totalPrice() {
            return this.calculateTotalPrice();
        },
        discountedPrice() {
            const saving = this.calculateDiscountAmount();
            const price = this.totalDeliveriesPrice - saving;
            return price;
        },
        validDiscount() {
            if ((this.selectedBoxName === PRODUCT_TYPE_BUMPER_MULTI && !this.multiCoupon.product_families.includes(PRODUCT_FAMILY_BUMPER)) ||
                (this.selectedBoxName === PRODUCT_TYPE_MULTI && !this.multiCoupon.product_families.includes(PRODUCT_FAMILY_STANDARD))) {
                return false;
            }
            const minBoxes = this.multiCoupon.minimumWeeks;
            return this.totalDeliveries >= minBoxes;
        },
        validSingleCoupon() {
            if ((this.selectedBoxName === PRODUCT_TYPE_SINGLE && !this.singleCoupon.product_families.includes(PRODUCT_FAMILY_STANDARD)) ||
                (this.selectedBoxName === PRODUCT_TYPE_BUMPER_SINGLE && !this.singleCoupon.product_families.includes(PRODUCT_FAMILY_BUMPER))) {
                return false;
            }
            return true;
        },
        singleGiftingText() {
            if (this.totalPrice === 0) {
                return this.$t('SINGLE_GIFT_COUPON_100');
            }
            return this.$t('SINGLE_GIFT_COUPON', { amount: this.couponDiscount(this.singleCoupon) });
        },
        multiGiftingText() {
            if (this.totalPrice === 0) {
                return this.$t('MULTI_GIFT_COUPON_100');
            }
            return this.$t('MULTI_GIFT_COUPON', { amount: this.couponDiscount(this.multiCoupon) });
        },
        getBoxName() {
            let boxName = this.products.find(item => item.type === this.selectedBoxName)?.boxName;
            this.$store.commit('gifting/setOrderedBoxName', boxName);
            return boxName;
        },
        isPopMulti() {
            return this.selectedBoxName === PRODUCT_TYPE_POP_MULTI;
        },
        isPopOneOff() {
            return this.selectedBoxName === PRODUCT_TYPE_POP_SINGLE;
        },
        isPopCoupon() {
            return this.isPopOneOff || this.isPopMulti;
        },
    },
};
</script>
